import React, { useContext, useState, useCallback, useRef, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Checkbox,
  CheckboxProps,
  mergeClasses,
  Body1Strong
} from '@fluentui/react-components';
import { Stack } from '@adsui/stack';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { usePoliticalAdsDeclarationStyles } from './styles';

export interface PoliticalAdsDeclarationProps {
  onChange: (value: boolean) => void;
  title: string;
  checkboxLabel: React.ReactNode;
  helpTopic?: string;
  helpAriaLabel?: string;
  disabled?: boolean;
  logToggledSelection: (value: boolean) => void;
}

export const PoliticalAdsDeclaration = (props: PoliticalAdsDeclarationProps) => {
  const {
    onChange,
    title,
    checkboxLabel,
    helpTopic,
    helpAriaLabel,
    disabled,
    logToggledSelection,
  } = props;
  const i18n = useContext(I18nContext);
  const classes = usePoliticalAdsDeclarationStyles();

  const [checked, setChecked] = useState<CheckboxProps['checked']>(false);
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setChecked(false);
    onChange(false);
  }, []);

  const handleCheckboxChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: CheckboxProps) => {
    if (!disabled) {
      logToggledSelection(!!data.checked);
      setChecked(!!data.checked);
      onChange(!!data.checked);
    }
  }, [onChange, disabled, logToggledSelection]);

  return (
    <div className="political-ads-declaration">
      <Stack>
        <Stack.Item className={classes.labelContent}>
          <Body1Strong className="political-ads-declaration-title">
            {title}
          </Body1Strong>
          {helpTopic && <HelpPopupInline topic={helpTopic} aria-label={helpAriaLabel || i18n.getString(_TL_('Learn more'))} />}
        </Stack.Item >
        <Stack.Item>
          <Checkbox
            tabIndex={0}
            onKeyDown={(e) => {
            if (e.key === ' ') {
              e.preventDefault();
              // Directly toggle the checkbox state
              const newChecked = !checked;
              setChecked(newChecked);
              onChange(newChecked);
            }
          }}
            ref={checkboxRef}
            className={mergeClasses('political-ads-declaration-checkbox', classes.checkbox)}
            label={<>{checkboxLabel}</>}
            checked={checked}
            aria-checked={checked}
            onChange={handleCheckboxChange}
            disabled={disabled}
            aria-disabled={disabled}
          />
        </Stack.Item>
      </Stack >
    </div>
  );
};


PoliticalAdsDeclaration.defaultProps = {
  helpTopic: undefined,
  helpAriaLabel: undefined,
  disabled: false,
};
